import type { Connector } from 'instantsearch.js';
import { noop } from 'instantsearch.js/es/lib/utils';

import {
  joinFiltersWithAnd,
  parseFilters,
  removeFilter,
} from '#src/lib/algolia.ts';

type ConnectorState = {
  refine(v: boolean): void;
  value: boolean;
};

export const connectExcludeFilter: Connector<
  {
    $$type: 'sorare.excludefilter';
    renderState: ConnectorState;
    indexRenderState: {
      exclusions?: {
        [searchParam: string]: boolean | undefined;
      };
    };
    indexUiState: {
      exclusions?: {
        [searchParam: string]: boolean | undefined;
      };
    };
  },
  { facet: string; excluded: string; searchParam: string }
> = (renderFn: any, unmountFn = noop) => {
  return widgetParams => {
    const { facet, excluded, searchParam } = widgetParams;

    const filter = `NOT ${facet}:${excluded}`;

    const connectorState: { refine?: (v: boolean) => void } = {};

    return {
      $$type: 'sorare.excludefilter',

      init(initOptions) {
        const { instantSearchInstance } = initOptions;

        renderFn(
          {
            ...this.getWidgetRenderState(initOptions),
            instantSearchInstance,
          },
          true
        );
      },

      render(renderOptions) {
        const { instantSearchInstance } = renderOptions;

        renderFn(
          {
            ...this.getWidgetRenderState(renderOptions),
            instantSearchInstance,
          },
          false
        );
      },

      dispose({ state }) {
        unmountFn();
        return state;
      },

      getWidgetRenderState({ results, helper }) {
        if (!connectorState.refine) {
          connectorState.refine = (value: boolean) => {
            let filters = removeFilter(helper.state.filters, filter);
            if (!value) {
              filters = joinFiltersWithAnd([...parseFilters(filters), filter]);
            }

            helper.setQueryParameter('filters', filters);
            helper.search();
          };
        }

        if (!results) {
          return { value: false, refine: connectorState.refine, widgetParams };
        }

        // eslint-disable-next-line no-underscore-dangle
        const value = !parseFilters(results._state.filters).includes(filter);

        return {
          value,
          refine: connectorState.refine,
          widgetParams,
        };
      },

      getRenderState(renderState, renderOptions) {
        const { value = false } = this.getWidgetRenderState(renderOptions);

        return {
          ...renderState,
          [searchParam]: value,
        };
      },

      // route to state
      getWidgetUiState(uiState, { searchParameters }) {
        const value = !parseFilters(searchParameters.filters).includes(filter);

        return {
          ...uiState,
          exclusions: {
            ...uiState.exclusions,
            [searchParam]: value,
          },
        };
      },

      // state to route
      getWidgetSearchParameters(searchParameters, { uiState }) {
        const value = !!uiState.exclusions?.[searchParam];

        searchParameters.filters = removeFilter(
          searchParameters.filters,
          filter
        );

        if (value) {
          return searchParameters;
        }
        searchParameters.filters = joinFiltersWithAnd([
          ...parseFilters(searchParameters.filters),
          filter,
        ]);

        return searchParameters;
      },
    };
  };
};
